import { useIsShopsite } from "@context/DomainContext";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { joinClassNames } from "@utils/helpers";
import { motion } from "framer-motion";
import React from "react";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

interface StyleProps {
  shopsiteClasses?: string;
  whitelabelClasses?: string;
  sharedClasses?: string;
}

const DialogPortal = ({
  children,
  ...props
}: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>
    <div className="fixed inset-0 px-6 sm:px-0 z-50 flex items-start justify-center sm:items-center">
      {children}
    </div>
  </DialogPrimitive.Portal>
);
DialogPortal.displayName = DialogPrimitive.Portal.displayName;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={joinClassNames(
      "fixed inset-0 z-60 bg-black/40",
      className ?? ""
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface ContentWithHandleClose extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  handleClose: (e: any) => void;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  ContentWithHandleClose &
    StyleProps & {
      sharedOverlayClasses?: string;
      shopsiteOverlayClasses?: string;
      whitelabelOverlayClasses?: string;
    }
>(
  (
    {
      className = "",
      children,
      handleClose,
      sharedClasses = "",
      shopsiteClasses = "",
      whitelabelClasses = "",
      sharedOverlayClasses = "",
      shopsiteOverlayClasses = "",
      whitelabelOverlayClasses = "",
      ...props
    },
    ref
  ) => {
    const isShopsite = useIsShopsite();
    const baseClasses =
      "fixed z-60 mx-auto max-md:m-2 max-md:bottom-4 max-w-7xl max-md:w-[90dvw] max-md:max-h-[95dvh] sm:max-w-lg gap-4 overflow-hidden";
    const shopsiteBaseClasses = "bg-white rounded-3xl shadow-modal";
    const overlayClassName = joinClassNames(
      sharedOverlayClasses,
      isShopsite ? shopsiteOverlayClasses : whitelabelOverlayClasses
    );
    const contentClassName = joinClassNames(
      baseClasses,
      isShopsite ? shopsiteBaseClasses : "",
      className,
      sharedClasses,
      isShopsite ? shopsiteClasses : whitelabelClasses
    );

    return (
      <DialogPortal
        forceMount={props.forceMount}
        container={document.getElementById(
          isShopsite ? "shopsite-root-element" : "whitelabel-root-element"
        )}
      >
        <DialogOverlay
          asChild
          forceMount
          onClick={handleClose}
          className={overlayClassName}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.95 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          />
        </DialogOverlay>
        <DialogPrimitive.Content
          ref={ref}
          asChild
          className={contentClassName}
          aria-describedby={props["aria-describedby"] ?? undefined}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={joinClassNames(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className ?? ""
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={joinClassNames(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className ?? ""
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogClose = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close ref={ref} {...props} className={className} />
));

DialogClose.displayName = DialogPrimitive.Close.displayName;

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & StyleProps
>(
  (
    {
      className = "",
      sharedClasses = "",
      shopsiteClasses = "",
      whitelabelClasses = "",
      ...props
    },
    ref
  ) => {
    const isShopsite = useIsShopsite();

    const shopsiteBaseClasses = "text-lg font-semibold";
    const titleClassName = joinClassNames(
      isShopsite ? shopsiteBaseClasses : "",
      className,
      sharedClasses,
      isShopsite ? shopsiteClasses : whitelabelClasses
    );

    return (
      <DialogPrimitive.Title ref={ref} className={titleClassName} {...props} />
    );
  }
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> &
    StyleProps
>(
  (
    {
      className = "",
      sharedClasses = "",
      shopsiteClasses = "",
      whitelabelClasses = "",
      ...props
    },
    ref
  ) => {
    const isShopsite = useIsShopsite();
    const shopsiteBaseClasses = "text-sm text-gray-500";

    return (
      <DialogPrimitive.Description
        ref={ref}
        className={joinClassNames(
          isShopsite ? shopsiteBaseClasses : "",
          className,
          sharedClasses,
          isShopsite ? shopsiteClasses : whitelabelClasses
        )}
        {...props}
      />
    );
  }
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
};
